import { createFileRoute, isRedirect, notFound, redirect } from "@tanstack/react-router";
import { isApiError } from "@majourneys/api-client";
import { activitiesApi } from "../../../features/activities/api/activities.api";
import {
  activityHrefParts,
  resolveLegacyActivitySlug,
} from "../../../features/activities/lib/activity-href";
import { transferHubRedirect } from "../../../features/transport/lib/hub-redirect";
import { ErrorPage } from "../../../features/errors";

/**
 * Legacy entry point: /<lang>/<slug>.
 *
 * Resolution order:
 *   1. Activity slug → 301 to canonical /<lang>/<city>/<category>/<slug>.
 *   2. City slug     → 301 to /<lang>/place/<slug>.
 *   3. Category slug → 301 to /<lang>/category/<slug>.
 *   4. Otherwise notFound() so SSR returns a real 404.
 *
 * Short links like /restaurants (bare host → /en/restaurants) land on step 3,
 * which is what social posts link to; the query (utm_*) is kept on every hop.
 *
 * Static-page slugs (discover, sign-in, …) match their own file routes
 * first — TanStack prefers literal segments over `$slug`, so they never
 * reach this loader.
 */
export const Route = createFileRoute("/$lang/_layout/$slug/")({
  beforeLoad: async ({ params }) => {
    const { lang } = params;
    const slug = resolveLegacyActivitySlug(params.slug);
    let activity: Awaited<ReturnType<typeof activitiesApi.getBySlug>>["data"] | null = null;
    try {
      const response = await activitiesApi.getBySlug(slug);
      activity = response.data;
    } catch (error) {
      if (!(isApiError(error) && error.status === 404)) throw error;
      // Not an activity. Try city, then category, before giving up.
      try {
        const citiesResp = await activitiesApi.listCities(lang);
        const cities = (citiesResp.data ?? []) as ReadonlyArray<{ slug: string }>;
        if (cities.some((c) => c.slug === slug)) {
          throw redirect({
            to: "/$lang/place/$city",
            params: { lang, city: slug },
            search: (prev: Record<string, unknown>) => prev,
            replace: true,
            statusCode: 301,
          });
        }
      } catch (e) {
        if (isRedirect(e)) throw e;
      }
      try {
        const catsResp = await activitiesApi.listCategories(undefined, lang);
        const cats = (catsResp.data ?? []) as ReadonlyArray<{ slug: string }>;
        if (cats.some((c) => c.slug === slug)) {
          throw redirect({
            to: "/$lang/category/$category",
            params: { lang, category: slug },
            search: (prev: Record<string, unknown>) => prev,
            replace: true,
            statusCode: 301,
          });
        }
      } catch (e) {
        if (isRedirect(e)) throw e;
      }
      throw notFound();
    }
    if (!activity?.slug) throw notFound();
    // A transfer route has no page: straight to the hub, one hop, not via
    // the canonical URL.
    const hub = transferHubRedirect(activity as Parameters<typeof transferHubRedirect>[0], lang);
    if (hub) throw redirect(hub);
    const aMode = (activity as { mode?: string }).mode ?? "bookable";
    const aSegment = (activity as { segment?: string | null }).segment ?? null;
    const parts = activityHrefParts({
      lang,
      citySlug: activity.city?.slug ?? null,
      categorySlug: activity.category?.slug ?? null,
      segment: aMode === "explore" ? aSegment : null,
      slug: activity.slug,
    });
    throw redirect({
      to: parts.to,
      params: parts.params,
      search: (prev: Record<string, unknown>) => prev,
      replace: true,
      statusCode: 301,
    });
  },
  component: () => <ErrorPage status={404} />,
});
